"use client";
import React, { useEffect, useState } from "react";
import { formatOdd, scrollToSection } from "@/utils";
import TeamLogo from "@/components/team/logo";
import TeamDisplay from "@/components/team/display";
import {
  BestOdds,
  GameAlignmentType,
  GameDetail,
  LineType,
  ScoreboardGame,
  buildOdd,
  isEmpty,
} from "common";
import OddsCell from "../odds/cell";
import styles from "@/components/game/card/hero.module.css";
import { getGameDetailBestOdds, getTotalScores } from "@/utils/game";
import Button from "@/components/primitives/button";
import GameOddsChartTabber from "../performance-chart";
import PickChip from "../detail/pick-chip";
import { useOddsFormat } from "@/utils/providers/odds-format";
import Link from "next/link";
import { useGame } from "@/utils/providers/game";
import mlClient from "@/ml-client";

export interface MockColumnProps {
  height?: "md" | "sm";
  header: React.ReactNode;
  cell: React.ReactNode[];
}
export function MockColumn(props: MockColumnProps) {
  const height = props.height === "sm" ? "h-[32px]" : "h-[48px]";
  return (
    <div
      className={`flex flex-row w-full justify-between desktop-grid:flex-col max-w-xs desktop-grid:max-w-[260px] ${height} desktop-grid:h-[156px] items-center ${
        props.header === "team"
          ? "desktop-grid:items-start mb-0 desktop-grid:mb-0"
          : "desktop-grid:items-center"
      }`}
    >
      <div className="order-2 flex items-center justify-center desktop-grid:justify-start desktop-grid:order-1 text-label-sm-medium uppercase text-secondary px-3 py-3 h-[48px]">
        <div className="w-full no-link">
          {props.header === "team" ? undefined : props.header}
        </div>
      </div>
      {props.cell.map((cell, i) => (
        <div
          key={i}
          className={`no-link ${
            i === 0 ? "order-1 desktop-grid:order-2" : "order-3"
          } text-sm-medium text-dark min-w-[81px] flex justify-center items-center`}
        >
          {cell}
        </div>
      ))}
    </div>
  );
}

function GameCardHero() {
  const { oddsFormat } = useOddsFormat();
  const ctx = useGame();
  const [bestOdds, setBestOdds] = useState<BestOdds[] | null>(null);

  async function getBestOdds() {
    try {
      const res = await mlClient.get(
        `/api/sports/${ctx.game.league}/game-detail/${ctx.game.id}/best-odds`
      );
      console.log("bestOddsData", res);
      if (res.ok) {
        setBestOdds(res.data as BestOdds[]);
      }
    } catch (err) {
      console.error("Failed to fetch best odds");
    }
  }

  useEffect(() => {
    getBestOdds();
  }, [ctx.game.id]);

  console.log('bestOdds', bestOdds);
  let bestOdd: BestOdds | null = null;
  let formattedOdd;
  if (
    bestOdds &&
    bestOdds.length > 0 &&
    (ctx.game.status === "Scheduled" || ctx.game.status === "InProgress")
  ) {
    bestOdd = bestOdds[0];
    formattedOdd = formatOdd(oddsFormat, bestOdd.odd);
  }
  console.log('bestOdd', bestOdd);

  return (
    <>
      {bestOdd && (
        <div className="relative z-[2] flex justify-center items-center mb-8">
          <a
            href={bestOdd?.sportsbookUrl || "/"}
            target="_blank"
            className=" bg-[url('/images/dark-pattern-bg.png')] h-12 mx-4 max-w-[350px] w-full rounded-lg border border-white/25 flex flex-row px-4 items-center gap-2"
          >
            <div className="text-label-lg-bold max-w-[34px] uppercase text-white">
              Best Odds
            </div>
            <div className="bg-white/25 h-4 w-px"></div>
            <div className="flex flex-row justify-between items-center gap-2 w-full">
              <div className="flex flex-col">
                <div className="flex flex-row items-center">
                  <img
                    src={bestOdd?.sportsbookLogoUrl}
                    width={32}
                    height={32}
                  />
                  <div className="flex flex-col ml-2">
                    <div className="flex flex-row text-white gap-1 items-center">
                      <div className="text-sm font-bold hidden xs:inline-block">
                        {bestOdd?.sportsbook}
                      </div>
                      <div className="flex flex-row items-start gap-1">
                        <div className="text-sm font-bold">
                          {formattedOdd?.type === LineType.Total ||
                          bestOdd.country !== "United States"
                            ? ""
                            : formattedOdd?.alignment === GameAlignmentType.Away
                            ? ctx.game.awayTeam.abbreviation
                            : ctx.game.homeTeam.abbreviation}
                        </div>
                        {bestOdd.country === "United States" && (
                          <div className="text-sm font-bold text-">
                            {formattedOdd?.type === LineType.Moneyline
                              ? formattedOdd?.payout
                              : formattedOdd?.value}{" "}
                            <span className="text-xs font-bold text-secondary">
                              {formattedOdd?.type === LineType.Moneyline
                                ? undefined
                                : formattedOdd?.payout}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-xs font-bold text-secondary italic">
                      BET WITH REAL MONEY{" "}
                    </div>
                  </div>
                </div>
              </div>
              <Link
                href={bestOdd?.sportsbookUrl || "/"}
                target="_blank"
                className="btn btn-2xs btn-filled-primary"
              >
                Bet {ctx.game.status === "InProgress" ? "Live" : "Now"}
              </Link>
            </div>
          </a>
        </div>
      )}
      <div className="shadow-card rounded-xl desktop-grid:mt-0 flex flex-col relative pt-4 px-4 pb-2 desktop-grid:pb-6 bg-surface z-[1]">
        <div className="desktop-grid:hidden absolute left-0 right-0 mx-auto -top-4 flex justify-center">
          <PickChip
            showAvatars
            idKey={"4"}
            totalPicks={ctx.game.totalPicks}
            avatars={ctx.game.avatars || []}
            totalBettors={ctx.game.totalBettors || 0}
            totalComments={ctx.game.discussion.total_comments || 0}
            totalPicksForSale={ctx.game.totalPicksForSale}
            side="right"
            dark
          />
        </div>
        <div className="flex items-center flex-col desktop-grid:flex-row z-[1] justify-center desktop-grid:justify-between max-w-[300px] mx-auto desktop-grid:max-w-none desktop-grid:mx-0 text-center gap-x-3">
          <div
            className={`text-headline-bold leading-none desktop-grid:text-headline-lg whitespace-nowrap gap-1 uppercase text-dark items-center flex`}
          >
            {(ctx.closed || ctx.game.status === "InProgress") &&
            !ctx.exception ? (
              <div className="flex items-center gap-x-1 gap-y-4 mt-4 desktop-grid:mt-0">
                <span>Boxscore</span>
              </div>
            ) : (
              <div className="flex items-end gap-x-1 gap-y-0 mt-4 desktop-grid:mt-0 flex-wrap justify-center text-center desktop-grid:text-left max-w-[180px] desktop-grid:max-w-none">
                <span className="hidden desktop-grid:block">
                  {ctx.game.awayTeam.name}
                </span>
                <span className="text-title-medium pt-1 hidden desktop-grid:block">
                  vs.
                </span>
                <span className="hidden desktop-grid:block">
                  {ctx.game.homeTeam.name}
                </span>
                <div className="max-w-[100px] whitespace-normal absolute top-6 desktop-grid:max-w-none desktop-grid:static">
                  Pregame Odds
                </div>
              </div>
            )}
          </div>
          <div className="hidden desktop-grid:flex flex-row items-center gap-1">
            {ctx.game.status !== "Scheduled" && (
              <Button
                variant="card"
                onClick={() => scrollToSection("game-detail-odds", 132)}
              >
                Odds
              </Button>
            )}
            <Button
              variant="card"
              onClick={() => scrollToSection("game-detail-pick-activity", 132)}
            >
              Picks
            </Button>
            {ctx.game.status === "Scheduled" ? (
              <Button
                variant="card"
                onClick={() =>
                  scrollToSection("game-detail-starting-lineup", 132)
                }
              >
                Lineup
              </Button>
            ) : undefined}
            <Button
              variant="card"
              onClick={() =>
                scrollToSection("game-detail-stats-comparison", 132)
              }
            >
              Stats
            </Button>
            {ctx.game.status === "Scheduled" && (
              <Button
                variant="card"
                onClick={() => scrollToSection("latest-injuries", 132)}
              >
                Injuries
              </Button>
            )}
          </div>
        </div>
        <div className="-mt-2 desktop-grid:mt-0 flex flex-col desktop-grid:flex-row justify-between w-full md:max-w-none desktop-grid:gap-4 items-center desktop-grid:items-end">
          <>
            {ctx.game.status === "Scheduled" && (
              <div className="desktop-grid:hidden flex flex-col w-full desktop-grid:max-w-[240px] items-center desktop-grid:items-start -mt-2 desktop-grid:mt-2">
                <MockColumn
                  header="team"
                  cell={[
                    <>
                      <div className="hidden desktop-grid:block max-h-[48px]">
                        <TeamDisplay
                          sport={ctx.game.league}
                          orientation="horizontal"
                          team={ctx.game.awayTeam}
                          size="md"
                          variant="location-name"
                        />
                      </div>
                      <div className="block desktop-grid:hidden">
                        <span className="text-center text-title-lg-medium">
                          {ctx.game.awayTeam.abbreviation}
                        </span>
                        {/* <TeamLogo team={data.awayTeam} size="md" /> */}
                      </div>
                    </>,
                    <>
                      <div className="hidden desktop-grid:block max-h-[48px]">
                        <TeamDisplay
                          sport={ctx.game.league}
                          orientation="horizontal"
                          team={ctx.game.homeTeam}
                          size="md"
                          variant="location-name"
                        />
                      </div>
                      <div className="block desktop-grid:hidden">
                        <span className="text-center text-title-lg-medium">
                          {ctx.game.homeTeam.abbreviation}
                        </span>

                        {/* <TeamLogo team={data.homeTeam} size="md" /> */}
                      </div>
                    </>,
                  ]}
                />
              </div>
            )}
            <div
              id="boxscore-team-column"
              className="hidden desktop-grid:flex flex-col w-full desktop-grid:max-w-[320px] items-center desktop-grid:items-start"
            >
              <MockColumn
                header="team"
                cell={[
                  <>
                    <div className="hidden desktop-grid:block max-h-[48px]">
                      <TeamDisplay
                        sport={ctx.game.league}
                        orientation="horizontal"
                        team={ctx.game.awayTeam}
                        size="md"
                        variant="location-name"
                      />
                    </div>
                    <div className="block desktop-grid:hidden">
                      <TeamLogo team={ctx.game.awayTeam} size="lg" />
                    </div>
                  </>,
                  <>
                    <div className="hidden desktop-grid:block max-h-[48px]">
                      <TeamDisplay
                        sport={ctx.game.league}
                        orientation="horizontal"
                        team={ctx.game.homeTeam}
                        size="md"
                        variant="location-name"
                      />
                    </div>
                    <div className="block desktop-grid:hidden">
                      <TeamLogo team={ctx.game.homeTeam} size="lg" />
                    </div>
                  </>,
                ]}
              />
            </div>
            {ctx.game.status === "Scheduled" || ctx.exception ? (
              <>
                <div className="flex flex-col desktop-grid:flex-row items-center desktop-grid:items-start gap-0 desktop-grid:gap-3 w-full max-w-xs desktop-grid:max-w-[300px]">
                  <MockColumn
                    header={<div className="w-full text-center">Moneyline</div>}
                    cell={[
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="away"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Moneyline,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Moneyline,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="home"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Moneyline,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Moneyline,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                    ]}
                  />
                  <MockColumn
                    header={<div className="w-full text-center">Spread</div>}
                    cell={[
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="away"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Spread,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Spread,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="home"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Spread,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Spread,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                    ]}
                  />
                  <MockColumn
                    header={
                      <div className="w-full text-center">Total (O/U)</div>
                    }
                    cell={[
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="away"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Total,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Total,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                      <div className="h-[54px] flex items-center justify-center">
                        <OddsCell
                          alignment="home"
                          game={ctx.game}
                          odd={buildOdd(
                            LineType.Total,
                            GameAlignmentType.Home,
                            ctx.game.odds
                          )}
                          againstOdd={buildOdd(
                            LineType.Total,
                            GameAlignmentType.Away,
                            ctx.game.odds
                          )}
                          startsNow={ctx.startsNow}
                        />
                      </div>,
                    ]}
                  />
                </div>
                {/* TABLE DIVIDER */}
                <div className="block desktop-grid:hidden w-[calc(100%+32px)] h-px bg-surface-500 my-2"></div>
                <div className="flex flex-col desktop-grid:flex-row gap-x-2 w-full max-w-xs mx-auto desktop-grid:max-w-[180px]">
                  <MockColumn
                    height="sm"
                    header={<div className="w-full text-center">Last 10</div>}
                    cell={[
                      <div className="flex items-center justify-center h-[54px]">
                        {ctx.game.league === "nhl" ? (
                          <>
                            {ctx.game.awayLast10?.wins || 0}-
                            {ctx.game.awayLast10?.losses || 0}-
                            {ctx.game.awayLast10?.overtimeLosses || 0}
                          </>
                        ) : (
                          <>
                            {ctx.game.awayLast10?.wins || 0}-
                            {ctx.game.awayLast10?.losses || 0}
                          </>
                        )}
                      </div>,
                      <div className="flex items-center justify-center h-[54px]">
                        {ctx.game.league === "nhl" ? (
                          <>
                            {ctx.game.homeLast10?.wins || 0}-
                            {ctx.game.homeLast10?.losses || 0}-
                            {ctx.game.homeLast10?.overtimeLosses || 0}
                          </>
                        ) : (
                          <>
                            {ctx.game.homeLast10?.wins || 0}-
                            {ctx.game.homeLast10?.losses || 0}
                          </>
                        )}
                      </div>,
                    ]}
                  />
                  <MockColumn
                    height="sm"
                    header={
                      <div className="w-full text-center">Last 10 ATS</div>
                    }
                    cell={[
                      <div className="flex items-center justify-center h-[54px]">
                        {ctx.game.awayAts.wins}-{ctx.game.awayAts.losses}-
                        {ctx.game.awayAts.ties}
                      </div>,
                      <div className="flex items-center justify-center h-[54px]">
                        {ctx.game.homeAts.wins}-{ctx.game.homeAts.losses}-
                        {ctx.game.homeAts.ties}
                      </div>,
                    ]}
                  />
                </div>
              </>
            ) : ctx.closed || ctx.game.status === "InProgress" ? (
              <>
                <div className="hidden desktop-grid:block">
                  <Boxscore game={ctx.game} mobile={false} />
                </div>
                <div className="block desktop-grid:hidden">
                  <Boxscore game={ctx.game} mobile={true} />
                </div>
              </>
            ) : (
              <></>
            )}
          </>
          <>
            {/* TABLE DIVIDER */}
            <div
              className={`${
                ctx.game.status === "Scheduled"
                  ? "block desktop-grid:hidden"
                  : "hidden"
              } w-[calc(100%+32px)]  h-px bg-surface-500 my-2`}
            ></div>
            <div
              className={`${
                ctx.game.status === "Scheduled"
                  ? "flex"
                  : "hidden desktop-grid:flex"
              } flex-col w-[calc(100%+32px)] h-[144px] max-w-xs`}
            >
              <GameOddsChartTabber
                defaultOdd={
                  ctx.game.league === "nfl" || ctx.game.league === "nba"
                    ? "spread"
                    : "moneyline"
                }
                game={ctx.game}
              />
              {/* {data.league === "nfl" || data.league === "nba" ? (
                <GamePerformanceChart
                  label="Spread %"
                  awaySpread={buildOdd("spread", "away", data.odds)}
                  awaySpreadPct={calculatePercentage(
                    data.awaySpreadPicks,
                    data.totalSpreadPicks
                  )}
                  awayTeam={data.awayTeam}
                  homeSpread={buildOdd("spread", "home", data.odds)}
                  homeSpreadPct={calculatePercentage(
                    data.homeSpreadPicks,
                    data.totalSpreadPicks
                  )}
                  homeTeam={data.homeTeam}
                />
              ) : (
                <GamePerformanceChart
                  label="Moneyline %"
                  awaySpread={buildOdd("moneyline", "away", data.odds)}
                  awaySpreadPct={calculatePercentage(
                    data.awayMoneylinePicks,
                    data.totalMoneylinePicks
                  )}
                  awayTeam={data.awayTeam}
                  homeSpread={buildOdd("moneyline", "home", data.odds)}
                  homeSpreadPct={calculatePercentage(
                    data.homeMoneylinePicks,
                    data.totalMoneylinePicks
                  )}
                  homeTeam={data.homeTeam}
                />
              )} */}
            </div>
          </>

          {/* TABLE DIVIDER */}
          <div
            className={`${
              ctx.game.status === "Scheduled"
                ? "block desktop-grid:hidden"
                : "hidden"
            } w-[calc(100%+32px)] h-px bg-surface-500 my-2`}
          ></div>
          <div
            className={`my-2 flex desktop-grid:hidden justify-center text-label-lg-medium text-secondary-300`}
          >
            {ctx.game.temperature && <span>{ctx.game.temperature}°</span>}{" "}
            {ctx.game.stadium?.name}
          </div>
          {/* TABLE DIVIDER */}
          <div className="block desktop-grid:hidden w-[calc(100%+32px)] h-px bg-surface-500 my-2"></div>
          <div className="flex desktop-grid:hidden flex-row items-center gap-1">
            {ctx.game.status !== "Scheduled" && (
              <Button
                variant="card"
                onClick={() => scrollToSection("game-detail-odds", 132)}
              >
                Odds
              </Button>
            )}
            <Button
              variant="card"
              onClick={() => scrollToSection("game-detail-pick-activity", 132)}
            >
              Picks
            </Button>
            {ctx.game.status === "Scheduled" ? (
              <Button
                variant="card"
                onClick={() =>
                  scrollToSection("game-detail-starting-lineup", 132)
                }
              >
                Lineup
              </Button>
            ) : undefined}
            <Button
              variant="card"
              onClick={() =>
                scrollToSection("game-detail-stats-comparison", 132)
              }
            >
              Stats
            </Button>
            {ctx.game.status === "Scheduled" && (
              <Button
                variant="card"
                onClick={() => scrollToSection("latest-injuries", 132)}
              >
                Injuries
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export function Boxscore({
  game,
  mobile,
  size = "md",
}: {
  game: ScoreboardGame;
  mobile?: boolean;
  size?: "sm" | "md";
}) {
  const ctx = useGame();
  // const exception = isGameException(game.status);
  // const closed: boolean = isGameClosed(game.status);
  // const live: LiveGame | undefined = isGameLive(
  //   game,
  //   undefined,
  //   game.series ? 3 : undefined
  // );
  const totalScore: { away: number; home: number } = getTotalScores(
    game.period
  );
  console.log("current game from boxscore", ctx.game);
  // const livePeriod = getLivePeriod(game.period);
  // const latestPeriod: GamePeriod = game.period[game.period.length-1];
  // const botInningNotNecessary: boolean = latestPeriod.value && latestPeriod.value >= 9 &&

  return (
    <div
      className="table-container w-full desktop-grid:w-auto justify-center mt-2"
      style={{ maxWidth: ctx.game.league === "mlb" ? "600px" : "400px" }}
    >
      <table
        className={`${styles.table} ${size === "md" ? styles.md : styles.sm} ${
          size === "sm" && (ctx.closed || ctx.exception)
            ? styles.final
            : undefined
        }`}
      >
        <thead>
          <tr>
            {mobile && <th style={{ minWidth: "36px", width: "36px" }}></th>}
            {game.period.map((period, i) => (
              <>
                <th
                  className={`
                    ${
                      ctx.exception
                        ? styles.disabled
                        : i <= ctx.game.currentPeriodIdx || ctx.closed
                        ? styles.past
                        : undefined
                    }
                      ${
                        i === ctx.game.currentPeriodIdx && !ctx.closed
                          ? styles.liveHeader
                          : undefined
                      }
                  `}
                  key={i}
                >
                  <div className="flex justify-center h-8 items-end">
                    <div
                      className={`h-full w-[32px] flex justify-center items-center rounded-tr-md rounded-tl-md ${
                        i === ctx.game.currentPeriodIdx && !ctx.closed
                          ? "bg-primary-500"
                          : "bg-transparent"
                      }`}
                    >
                      {period.value}
                    </div>
                  </div>
                </th>
              </>
            ))}
            {game.league === "mlb" ? (
              <>
                <th className={styles.greyHeader1}>R</th>
                <th className={styles.greyHeader2}>H</th>
                <th className={styles.greyHeader3}>E</th>
              </>
            ) : (
              <th className={styles.greyHeader4}>Total</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            {mobile && (
              <td className="flex justify-center items-center">
                <TeamLogo team={game.awayTeam} size="xs" />
              </td>
            )}
            {game.period.map((period, i) => {
              let awayScore = `${period.awayScore}`;
              // Shootout
              if (game.league === "nhl" && i === 4) {
                if (game.closed) {
                  const awayShootoutWin =
                    (period.awayScore || 0) > (period.homeScore || 0);
                  awayScore = `${awayShootoutWin ? `1` : `0`}`;
                }
              }
              return (
                <td
                  className={`${
                    ctx.exception
                      ? styles.disabled
                      : i <= ctx.game.currentPeriodIdx || ctx.closed
                      ? styles.past
                      : undefined
                  } ${
                    ctx.isLive &&
                    game.league === "mlb" &&
                    game.period[i].status === "Top"
                      ? styles.atBat
                      : undefined
                  }`}
                  key={i}
                >
                  <div>
                    {ctx.exception
                      ? "–"
                      : i === ctx.game.currentPeriodIdx || ctx.closed
                      ? awayScore
                      : i < ctx.game.currentPeriodIdx || ctx.closed
                      ? awayScore
                      : "–"}
                  </div>
                </td>
              );
            })}
            {game.league === "mlb" ? (
              <>
                <td className={styles.greyCell1}>{totalScore.away || 0}</td>
                <td className={styles.greyCell2}>{game.awayHits || 0}</td>
                <td className={styles.greyCell3}>{game.awayErrors || 0}</td>
              </>
            ) : (
              <td className={styles.greyCell4}>{ctx.game.awayScore}</td>
            )}
          </tr>
          <tr>
            {mobile && (
              <td className="flex justify-center items-center">
                <TeamLogo team={ctx.game.homeTeam} size="xs" />
              </td>
            )}
            {game.period.map((period, i) => {
              let homeScore = `${period.homeScore}`;
              // Shootout
              if (ctx.game.league === "nhl" && i === 4) {
                if (ctx.closed) {
                  const homeShootoutWin =
                    (period.homeScore || 0) > (period.awayScore || 0);
                  homeScore = `${homeShootoutWin ? `1` : `0`}`;
                }
              }
              return (
                <td
                  className={`${
                    ctx.exception
                      ? styles.disabled
                      : i <= ctx.game.currentPeriodIdx || ctx.closed
                      ? styles.past
                      : undefined
                  } ${
                    ctx.isLive &&
                    game.league === "mlb" &&
                    game.period[i].status === "Bot"
                      ? styles.atBat
                      : undefined
                  }`}
                  key={i}
                >
                  <div>
                    {((ctx.currentPeriod?.status === "Top" ||
                      ctx.currentPeriod?.status === "Mid") &&
                      ctx.game.currentPeriodIdx === i) ||
                    ctx.exception
                      ? "–"
                      : game.league === "mlb" &&
                        ctx.closed &&
                        i + 1 === 9 &&
                        totalScore.away < totalScore.home &&
                        game.period[i].status === "Top"
                      ? "X"
                      : i === ctx.game.currentPeriodIdx || ctx.closed
                      ? homeScore
                      : i < ctx.game.currentPeriodIdx || ctx.closed
                      ? homeScore
                      : "–"}
                  </div>
                </td>
              );
            })}
            {ctx.game.league === "mlb" ? (
              <>
                <td className={styles.greyCell1}>{totalScore.home || 0}</td>
                <td className={styles.greyCell2}>{ctx.game.homeHits || 0}</td>
                <td className={styles.greyCell3}>{ctx.game.homeErrors || 0}</td>
              </>
            ) : (
              <td className={styles.greyCell4}>{ctx.game.homeScore}</td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default GameCardHero;
